/**
 * RefereeDetailPage - Detailed view of a single referee.
 *
 * Admin-only page that displays:
 * - Referee details (name, phone, status, field, etc.)
 * - Shift management (add/delete shifts)
 * - Password reset functionality
 * - Edit capabilities for referee fields
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import ResetPasswordIcon from '@mui/icons-material/LockReset';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import TelegramIcon from '@mui/icons-material/Telegram';
import CopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { refereesApi, configApi } from '@/api/client';
import { EventLog } from '@/components/EventLog';
import type { Referee, RefereeShift } from '@/types';

interface Settings {
  tournament_date?: string;
}

// Status color mapping
const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  waiting: 'default',
  assigned: 'info',
  set_to_field: 'primary',
  running_errands: 'warning',
  done: 'success',
  broken: 'error',
  missing: 'error',
};

export function RefereeDetailPage() {
  const { refereeId } = useParams<{ refereeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [addShiftDialogOpen, setAddShiftDialogOpen] = useState(false);
  const [newShift, setNewShift] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '12:00',
    notes: '',
  });
  const [deleteShiftId, setDeleteShiftId] = useState<number | null>(null);

  const handleCopy = async (value: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const id = refereeId ? parseInt(refereeId, 10) : null;

  // Fetch settings to get tournament date
  const { data: settingsConfig } = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: () => configApi.get('settings'),
  });
  const settings = settingsConfig?.value as Settings | undefined;
  const defaultDate = settings?.tournament_date || new Date().toISOString().split('T')[0];

  // Fetch referee details
  const {
    data: referee,
    isLoading,
    error,
  } = useQuery<Referee>({
    queryKey: ['referee', id],
    queryFn: () => refereesApi.get(id!),
    enabled: !!id,
  });

  // Fetch referee shifts
  const { data: shifts = [] } = useQuery<RefereeShift[]>({
    queryKey: ['referee-shifts', id],
    queryFn: () => refereesApi.getShifts(id!),
    enabled: !!id,
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: () => refereesApi.resetPassword(id!),
    onSuccess: () => {
      setResetDialogOpen(false);
    },
  });

  // Create shift mutation
  const createShiftMutation = useMutation({
    mutationFn: (data: typeof newShift) => refereesApi.createShift(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referee-shifts', id] });
      setAddShiftDialogOpen(false);
      setNewShift({
        date: defaultDate,
        start_time: '09:00',
        end_time: '12:00',
        notes: '',
      });
    },
  });

  // Delete shift mutation
  const deleteShiftMutation = useMutation({
    mutationFn: (shiftId: number) => refereesApi.deleteShift(id!, shiftId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referee-shifts', id] });
      setDeleteShiftId(null);
    },
  });

  const handleResetPassword = () => {
    resetPasswordMutation.mutate();
  };

  const handleCreateShift = () => {
    createShiftMutation.mutate(newShift);
  };

  const handleDeleteShift = () => {
    if (deleteShiftId) {
      deleteShiftMutation.mutate(deleteShiftId);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !referee) {
    return (
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/referees')}
          sx={{ mb: 2 }}
        >
          Back to Referees
        </Button>
        <Alert severity="error">
          Referee not found or failed to load
        </Alert>
      </Box>
    );
  }

  const user = referee.user;

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/admin/referees')}
        sx={{ mb: 2 }}
      >
        Back to Referees
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          {user.first_name} {user.last_name}
        </Typography>
        <Chip
          label={referee.status_display || referee.status}
          color={STATUS_COLORS[referee.status] || 'default'}
          size="small"
        />
      </Box>

      {resetPasswordMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Password has been reset to "Salasana1"
        </Alert>
      )}

      {resetPasswordMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to reset password
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* User Information */}
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BadgeIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Username
                  </Typography>
                  <Typography variant="body1">{user.username}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Full Name
                  </Typography>
                  <Typography variant="body1">
                    {user.first_name} {user.last_name}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon color="action" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body1">{user.phone || '-'}</Typography>
                    {user.phone && (
                      <Tooltip title={copiedField === 'phone' ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(user.phone, 'phone')}
                          color={copiedField === 'phone' ? 'success' : 'default'}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{user.email || '-'}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TelegramIcon color="action" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Telegram
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body1">{user.telegram ? `@${user.telegram}` : '-'}</Typography>
                    {user.telegram && (
                      <Tooltip title={copiedField === 'telegram' ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(user.telegram, 'telegram')}
                          color={copiedField === 'telegram' ? 'success' : 'default'}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Referee Details */}
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Referee Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box>
                  <Chip
                    label={referee.status_display || referee.status}
                    color={STATUS_COLORS[referee.status] || 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Assigned Court
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">
                    {referee.field || '-'}
                  </Typography>
                  {referee.is_area_referee && (
                    <Chip label="Area Ref" size="small" color="info" />
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Field Group (Area)
                </Typography>
                <Typography variant="body1">
                  {referee.field_group_name || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  League Filter
                </Typography>
                <Typography variant="body1">
                  {referee.league_filter_display || referee.league_filter || 'All'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Comment
                </Typography>
                <Typography variant="body1">
                  {referee.comment || '-'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Shifts */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Shifts
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewShift(prev => ({ ...prev, date: defaultDate }));
                  setAddShiftDialogOpen(true);
                }}
              >
                Add Shift
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {shifts.length === 0 ? (
              <Typography color="text.secondary">
                No shifts scheduled
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell width={60}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shifts.map((shift) => (
                    <TableRow key={shift.id}>
                      <TableCell>{shift.start_time}</TableCell>
                      <TableCell>{shift.end_time}</TableCell>
                      <TableCell>{shift.notes || '-'}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteShiftId(shift.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>

        {/* Admin Actions */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Admin Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Button
              variant="outlined"
              color="warning"
              startIcon={<ResetPasswordIcon />}
              onClick={() => setResetDialogOpen(true)}
            >
              Reset Password
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Resets password to "Salasana1"
            </Typography>
          </Paper>
        </Box>

        {/* Event Log */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <EventLog params={{ referee_id: id! }} title="Referee Event Log" />
        </Box>
      </Box>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset the password for <strong>{user.username}</strong> ({user.first_name} {user.last_name})?
            <br /><br />
            The password will be set to: <strong>Salasana1</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPassword}
            disabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Shift Dialog */}
      <Dialog open={addShiftDialogOpen} onClose={() => setAddShiftDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Shift</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={newShift.start_time}
                onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Time"
                type="time"
                value={newShift.end_time}
                onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            <TextField
              label="Notes"
              value={newShift.notes}
              onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })}
              placeholder="Optional notes"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddShiftDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateShift}
            disabled={createShiftMutation.isPending}
          >
            {createShiftMutation.isPending ? 'Adding...' : 'Add Shift'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Shift Confirmation Dialog */}
      <Dialog open={deleteShiftId !== null} onClose={() => setDeleteShiftId(null)}>
        <DialogTitle>Delete Shift</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this shift?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteShiftId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteShift}
            disabled={deleteShiftMutation.isPending}
          >
            {deleteShiftMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

